import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Badge } from '../../../components/ui/badge';
import { 
  BeakerIcon, 
  ArrowRight, 
  Type, 
  Image, 
  FileText,
  Sparkles,
  Zap
} from 'lucide-react';
import DefaultLayout from '../../layout/DefaultLayout';
import { useAuth } from 'wasp/client/auth';
import TestConfigDialog from './TestConfigDialog';
import { useTranslation } from 'react-i18next';

// Test card type definition
interface TestItem {
  name: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  examples: string[];
}

const AdminOptimizationTestingPage = () => {
  const { t } = useTranslation();
  const { data: user, isLoading } = useAuth();
  const [selectedTest, setSelectedTest] = useState<TestItem | null>(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const predefinedTests: {
    titles: TestItem[];
    descriptions: TestItem[];
    visuals: TestItem[];
  } = {
    titles: [
      {
        name: t("optimizationTesting.title_length_optimization"),
        description: t("optimizationTesting.title_length_description"),
        impact: "medium" as const,
        examples: ["Current: Ultra-Light Running Shoes", "Variant: Nike Air Zoom Ultra-Light Memory Foam Running Shoes - All-Day Comfort"],
      },
      {
        name: t("optimizationTesting.benefit_first_titles"),
        description: t("optimizationTesting.benefit_first_description"),
        impact: "high" as const,
        examples: ["Current: Memory Foam Running Shoes", "Variant: Never Have Sore Feet Again - Memory Foam Runners"],
      },
      {
        name: t("optimizationTesting.social_proof_integration"),
        description: t("optimizationTesting.social_proof_integration_description"),
        impact: "high" as const,
        examples: ["Current: Organic Cotton Bedding Set", "Variant: 5-Star Rated Organic Cotton Bedding - 10,000+ Happy Sleepers"],
      },
      {
        name: t("optimizationTesting.seasonal_relevance"),
        description: t("optimizationTesting.seasonal_relevance_description"),
        impact: "medium" as const,
        examples: ["Current: Lightweight Beach Cover-Up", "Variant: Perfect Summer Beach Cover-Up - UPF50+ Sun Protection"],
      },
      {
        name: t("optimizationTesting.specificity_enhancement"),
        description: t("optimizationTesting.specificity_enhancement_description"),
        impact: "medium" as const,
        examples: ["Current: Wireless Earbuds", "Variant: 36-Hour Battery Wireless Earbuds with Active Noise Cancelling"],
      },
      {
        name: t("optimizationTesting.problem_solution_format"),
        description: t("optimizationTesting.problem_solution_description"),
        impact: "high" as const,
        examples: ["Current: Anti-Aging Night Cream", "Variant: Goodbye Wrinkles - Dermatologist-Approved Night Repair Cream"],
      }
    ],
    descriptions: [
      {
        name: t("optimizationTesting.structure_format"),
        description: t("optimizationTesting.structure_format_description"),
        impact: "medium" as const,
        examples: ["Current: • Ultra-light design\n• Memory foam comfort\n• All-day support", "Variant: Experience the perfect blend of lightweight design and all-day comfort..."],
      },
      {
        name: t("optimizationTesting.emotional_storytelling"),
        description: t("optimizationTesting.emotional_storytelling_description"),
        impact: "high" as const,
        examples: ["Current: Featuring 8mm heel drop, EVA foam technology...", "Variant: Transform your running experience with cloud-like comfort..."],
      },
      {
        name: t("optimizationTesting.user_scenario_integration"),
        description: t("optimizationTesting.user_scenario_description"),
        impact: "high" as const,
        examples: ["Current: High-capacity power bank with fast charging capabilities", "Variant: Perfect for long flights or camping trips - keep all your devices charged for up to a week"],
      },
      {
        name: t("optimizationTesting.social_proof_enhancement"),
        description: t("optimizationTesting.social_proof_enhancement_description"),
        impact: "high" as const,
        examples: ["Current: Premium quality dog food with balanced nutrition", "Variant: Join 50,000+ happy pet parents who've seen their dogs thrive on our vet-approved nutrition formula"],
      },
      {
        name: t("optimizationTesting.feature_benefit_pairing"),
        description: t("optimizationTesting.feature_benefit_description"),
        impact: "medium" as const,
        examples: ["Current: 4K resolution, 120Hz refresh rate, HDR support", "Variant: 4K resolution for crystal-clear movies, 120Hz for smooth gaming, HDR for vivid colors that pop"],
      },
      {
        name: t("optimizationTesting.comparison_integration"),
        description: t("optimizationTesting.comparison_integration_description"),
        impact: "high" as const,
        examples: ["Current: Professional-grade blender with 10 speed settings", "Variant: 2x quieter than leading brands, yet powerful enough to blend frozen fruits in seconds - perfect for early morning smoothies without waking the house"],
      }
    ],
    visuals: [
      {
        name: t("optimizationTesting.hero_image_context"),
        description: t("optimizationTesting.hero_image_description"),
        impact: "high" as const,
        examples: ["Current: Studio product shot", "Variant: Runner wearing shoes on trail"],
      },
      {
        name: t("optimizationTesting.gallery_sequence"),
        description: t("optimizationTesting.gallery_sequence_description"),
        impact: "medium" as const,
        examples: ["Current: Product details first", "Variant: Lifestyle context first"],
      },
      {
        name: t("optimizationTesting.size_reference_integration"),
        description: t("optimizationTesting.size_reference_description"),
        impact: "medium" as const,
        examples: ["Current: Product on white background", "Variant: Product shown next to common objects for size reference"],
      },
      {
        name: t("optimizationTesting.use_case_demonstration"),
        description: t("optimizationTesting.use_case_description"),
        impact: "high" as const,
        examples: ["Current: Kitchen mixer on counter", "Variant: Time-lapse sequence showing complete recipe preparation"],
      },
      {
        name: t("optimizationTesting.before_after_integration"),
        description: t("optimizationTesting.before_after_description"),
        impact: "high" as const,
        examples: ["Current: Product bottle display", "Variant: Split-screen showing before/after results"],
      },
      {
        name: t("optimizationTesting.feature_highlight_sequence"),
        description: t("optimizationTesting.feature_highlight_description"),
        impact: "medium" as const,
        examples: ["Current: Standard angle rotation", "Variant: Each key feature highlighted with callout annotation"],
      },
      {
        name: t("optimizationTesting.social_proof_visuals"),
        description: t("optimizationTesting.social_proof_visuals_description"),
        impact: "high" as const,
        examples: ["Current: Professional product photography", "Variant: Curated customer photos showing real usage"],
      }
    ]
  };

  if (isLoading) return null;

  const ImpactBadge = ({ impact }: { impact: 'high' | 'medium' | 'low' }) => {
    const colors = {
      high: 'bg-red-50 text-red-700 border border-red-200',
      medium: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      low: 'bg-green-50 text-green-700 border border-green-200'
    };
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colors[impact]}`}>
        <Sparkles className="w-3 h-3" />
        {impact === 'high' ? t("optimizationTesting.high_impact") : 
         impact === 'medium' ? t("optimizationTesting.medium_impact") : 
         t("optimizationTesting.low_impact")}
      </div>
    );
  };

  const TestCard = ({ test }: { test: TestItem }) => (
    <Card className="h-full hover:shadow-md transition-all duration-200 flex flex-col">
      <CardHeader className="pb-2">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <CardTitle className="text-xl font-semibold">{test.name}</CardTitle>
            <ImpactBadge impact={test.impact} />
          </div>
          <CardDescription className="text-sm text-gray-600">{test.description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex-1 pt-6">
        <div className="space-y-4">
          <div className="text-sm font-medium text-gray-600">{t("optimizationTesting.example_variants")}</div>
          <div className="flex flex-col gap-3">
            <div className="p-3 bg-gray-50 rounded-md text-sm border border-gray-100">
              {test.examples[0]}
            </div>
            <div className="flex justify-center">
              <div className="h-px w-2/3 bg-gray-200 relative">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2">
                  <ArrowRight className="text-gray-300 w-4 h-4" />
                </div>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-md text-sm border border-blue-100">
              {test.examples[1]}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="mt-auto pt-6">
        <Button 
          className="w-full gap-2"
          onClick={() => {
            setSelectedTest(test);
            setIsConfigOpen(true);
          }}
        >
          <Zap className="w-4 h-4" />
          {t("optimizationTesting.start_test")}
        </Button>
      </CardFooter>
    </Card>
  );

  if (isLoading) return null;
  if (!user) return null;
  return (
    <DefaultLayout user={user}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-blue-500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="8" height="18" rx="1" />
                <rect x="13" y="3" width="8" height="18" rx="1" />
              </svg>            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {t("optimizationTesting.content_testing_lab")}
              </h1>
              <p className="text-gray-500">
                {t("optimizationTesting.testing_lab_description")}
              </p>
            </div>
          </div>

          <Tabs defaultValue="titles" className="space-y-6">
            <TabsList className="inline-flex h-9 items-center justify-center rounded-lg bg-gray-100 p-1 text-gray-500 mx-auto">
              <TabsTrigger value="titles" className="inline-flex items-center gap-2 px-3">
                <Type className="h-4 w-4" /> {t("optimizationTesting.titles_tab")}
              </TabsTrigger>
              <TabsTrigger value="descriptions" className="inline-flex items-center gap-2 px-3">
                <FileText className="h-4 w-4" /> {t("optimizationTesting.descriptions_tab")}
              </TabsTrigger>
              <TabsTrigger value="visuals" className="inline-flex items-center gap-2 px-3">
                <Image className="h-4 w-4" /> {t("optimizationTesting.visuals_tab")}
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[700px] px-1">
              <TabsContent value="titles">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {predefinedTests.titles.map((test, i) => (
                    <TestCard key={i} test={test} />
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="descriptions">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {predefinedTests.descriptions.map((test, i) => (
                    <TestCard key={i} test={test} />
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="visuals">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {predefinedTests.visuals.map((test, i) => (
                    <TestCard key={i} test={test} />
                  ))}
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <TestConfigDialog 
            isOpen={isConfigOpen}
            onClose={() => setIsConfigOpen(false)}
            testConfig={selectedTest}
          />
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AdminOptimizationTestingPage;